import { useIntl } from '@capsicum/common';
import { type BaseTextInputIcon, useCopyToClipboard } from '@capsicum/common-web';
import { CopyClipboardIcon } from '@capsicum/common-web/icons';
import { type RecoveryKit } from '@capsicum/features/nc-onboarding/shared';
import { useOnboardingAnalytics } from '@capsicum/socios-core';
import { useState } from 'react';

const useCheckboxes = () => {
	const [{ isConfirmFundsLost, isConfirmSecuredKit, isConfirmSocios }, setIsConfirm] = useState({
		isConfirmFundsLost: false,
		isConfirmSecuredKit: false,
		isConfirmSocios: false
	});

	const handleCheckboxChange = (name: 'isConfirmFundsLost' | 'isConfirmSecuredKit' | 'isConfirmSocios') => {
		setIsConfirm(prevState => ({
			...prevState,
			[name]: !prevState[name]
		}));
	};

	return {
		handleCheckboxChange,
		isConfirmFundsLost,
		isConfirmSecuredKit,
		isConfirmSocios
	};
};

const useCopyRecoveryParamsToClipboard = (recoveryKit: RecoveryKit) => {
	const { $t } = useIntl();

	const intlTexts = {
		buttonCopySnack: $t('ncOnboarding.recoveryKit.buttonCopySnack')
	};

	const { copyToClipboard: copyRecoveryCodeToClipboard } = useCopyToClipboard({
		label: intlTexts.buttonCopySnack,
		value: recoveryKit.code
	});

	const { copyToClipboard: copyRecoveryKeyIdToClipboard } = useCopyToClipboard({
		label: intlTexts.buttonCopySnack,
		value: recoveryKit.keyId
	});

	const recoveryCodeCopyIcon: BaseTextInputIcon | undefined = {
		icon: CopyClipboardIcon,
		onClick: copyRecoveryCodeToClipboard,
		testID: 'ncRecoveryKit-recoveryCode-copy'
	};

	const recoveryKeyIdCopyIcon: BaseTextInputIcon | undefined = {
		icon: CopyClipboardIcon,
		onClick: copyRecoveryKeyIdToClipboard,
		testID: 'ncRecoveryKit-recoveryKeyId-copy'
	};

	return {
		recoveryCodeCopyIcon,
		recoveryKeyIdCopyIcon
	};
};

const useDownloadRecoveryKit = (recoveryKit: RecoveryKit, isNewUser: boolean) => {
	const { $t } = useIntl();
	const { sendRegistrationRecoveryKitDownloadEvent } = useOnboardingAnalytics();

	const intlTexts = {
		inputRecoveryCode: $t('ncOnboarding.recoveryKit.form.inputRecoveryCode'),
		inputRecoveryKeyId: $t('ncOnboarding.recoveryKit.form.inputRecoveryKeyId')
	};

	const handleClickDownload = () => {
		isNewUser && sendRegistrationRecoveryKitDownloadEvent();

		const file = new Blob(
			[
				`${intlTexts.inputRecoveryCode}: ${recoveryKit.code}  ${intlTexts.inputRecoveryKeyId}: ${recoveryKit.keyId}`
			],
			{
				type: 'text/plain'
			}
		);
		const downloadLink = document.createElement('a');

		downloadLink.href = URL.createObjectURL(file);
		downloadLink.target = '_blank';
		downloadLink.download = 'recovery-kit.txt';
		downloadLink.click();
	};

	return {
		handleClickDownload
	};
};

const useIntlTexts = () => {
	const { $t } = useIntl();

	return {
		buttonGenerate: $t('ncOnboarding.recoveryKit.generateRecoveryKit.title'),
		explanation: $t('ncOnboarding.recoveryKit.generateRecoveryKit.explanation'),
		title: $t('ncOnboarding.recoveryKit.generateRecoveryKit.title')
	};
};

export const useRecoveryKitGenerationUtils = (recoveryKit: RecoveryKit, isNewUser = false) => ({
	intlTexts: useIntlTexts(),
	...useCheckboxes(),
	...useCopyRecoveryParamsToClipboard(recoveryKit),
	...useDownloadRecoveryKit(recoveryKit, isNewUser)
});
